import { formatPropertyAddress, formatStreetAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import { SenderWarningBanner, VerifySenderButton } from './SenderWarning';
import { sendEmailRequest } from '../services/emailDeliveryService';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { decryptPassword } from '../utils/decrypt';
import { getPositiveUserId, getStoredUser, hasAdminAccess } from '../utils/authUser';
import { getCustomSignature, saveCustomSignature } from '../services/customSignatureService';
import ChatLeadCampaignBuilder from './ChatLeadCampaignBuilder';
import './EmailManager.css';

const TemplateScopeSelector = ({ name, value, onChange, personalDisabled = false, disabled = false }) => (
  <div className="template-scope-selector" role="radiogroup" aria-label="Template visibility">
    <label className={`scope-option ${value === 'agent' ? 'selected' : ''} ${personalDisabled || disabled ? 'disabled' : ''}`}>
      <input
        type="radio"
        name={name}
        value="agent"
        checked={value === 'agent'}
        disabled={personalDisabled || disabled}
        onChange={() => onChange('agent')}
      />
      <span>
        <strong>Personal</strong>
        <small>Only you can see and use this template.</small>
      </span>
    </label>
    <label className={`scope-option ${value === 'brokerage' ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}>
      <input
        type="radio"
        name={name}
        value="brokerage"
        checked={value === 'brokerage'}
        disabled={disabled}
        onChange={() => onChange('brokerage')}
      />
      <span>
        <strong>Brokerage-wide</strong>
        <small>Everyone at the brokerage can see and use it.</small>
      </span>
    </label>
  </div>
);

const EmailUiIcon = ({ name }) => {
  const paths = {
    mail: <><rect x="3" y="5" width="18" height="14" rx="3" /><path d="m5 8 7 5 7-5" /></>,
    dashboard: <><rect x="4" y="4" width="6" height="6" rx="1" /><rect x="14" y="4" width="6" height="6" rx="1" /><rect x="4" y="14" width="6" height="6" rx="1" /><rect x="14" y="14" width="6" height="6" rx="1" /></>,
    compose: <><path d="M4 20h4l11-11a2.8 2.8 0 0 0-4-4L4 16v4Z" /><path d="m13.5 6.5 4 4" /></>,
    upload: <><path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5" /><path d="M5 14v5h14v-5" /></>,
    library: <><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M8 8h8M8 12h8M8 16h5" /></>,
    automation: <><path d="M7 7h10v4" /><path d="m14 8 3 3 3-3M17 17H7v-4" /><path d="m10 16-3-3-3 3" /></>,
    image: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="2" /><path d="m4 17 5-4 3 2 3-3 5 5" /></>,
    home: <><path d="m3 11 9-7 9 7" /><path d="M5.5 9.5V20h13V9.5M9 20v-6h6v6" /></>,
    sparkle: <><path d="m12 3 1.3 4.2L17 9l-3.7 1.8L12 15l-1.3-4.2L7 9l3.7-1.8L12 3Z" /><path d="m18.5 14 .7 2.3 2.3.7-2.3.7-.7 2.3-.7-2.3-2.3-.7 2.3-.7.7-2.3Z" /></>,
    calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M7 3v4m10-4v4M3 10h18" /></>,
    refresh: <><path d="M20 7v5h-5" /><path d="M4 17v-5h5" /><path d="M6.1 8A7 7 0 0 1 18 7l2 5M18 16a7 7 0 0 1-11.9 1L4 12" /></>,
    send: <><path d="m3 11 18-8-7 18-3-7-8-3Z" /><path d="m11 14 4-4" /></>,
    signature: <><path d="M4 18c2.5-3.5 4.2-5.2 5.2-5.2 1.8 0-.3 4.2 1.5 4.2 1 0 2.1-2.4 3.3-2.4 1 0 .7 2.1 2 2.1 1.1 0 2.2-.6 4-1.7" /><path d="m15 5 2-2 4 4-2 2-4-4Z" /><path d="m14.8 5.2-5.6 5.6-.7 3.3 3.3-.7 5.6-5.6" /></>,
    code: <><path d="m8 8-4 4 4 4m8-8 4 4-4 4m-2-11-4 14" /></>
  };
  return <svg className="email-ui-icon" viewBox="0 0 24 24" aria-hidden="true">{paths[name]}</svg>;
};

const SIGNATURE_DOCUMENTS = [
  { key: 'iabs', label: 'Information About Brokerage Services' },
  { key: 'consumer_protection_notice', label: 'Consumer Protection Notice' }
];

const createSignatureForm = (profile, currentUser) => ({
  name: profile?.name || currentUser?.name || currentUser?.username || '',
  phone: profile?.phone || currentUser?.phoneNumber || '',
  email: profile?.email || currentUser?.email || '',
  city: profile?.city || SITE_CONFIG.AGENT?.OFFICE_CITY || '',
  state: profile?.state || SITE_CONFIG.STATE || '',
  brokerage: profile?.brokerage || SITE_CONFIG.AGENT?.BROKERAGE || '',
  documentUrls: SIGNATURE_DOCUMENTS.reduce((urls, document) => ({
    ...urls,
    [document.key]: profile?.documents?.find(item => item.key === document.key)?.url || ''
  }), {})
});

const isValidEmailAddress = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const isValidHttpsUrl = (value) => {
  try {
    return new URL(value).protocol === 'https:';
  } catch (error) {
    return false;
  }
};

const EmailManager = () => {
  const currentUser = getStoredUser();
  const currentUserId = getPositiveUserId(currentUser);
  const canManageSignature = hasAdminAccess(currentUser);
  const defaultScope = currentUserId ? 'agent' : 'brokerage';
  const [activeTab, setActiveTab] = useState(() => {
    const requestedTab = new URLSearchParams(window.location.search).get('tab');
    return ['create', 'upload', 'templates', 'automations'].includes(requestedTab) ? requestedTab : 'create';
  });
  const [emailContent, setEmailContent] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [improvedHtml, setImprovedHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewTab, setPreviewTab] = useState('preview');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [uploadTemplateName, setUploadTemplateName] = useState('');
  const [uploadHtml, setUploadHtml] = useState('');
  const [uploadPreviewTab, setUploadPreviewTab] = useState('html');
  const [savingUpload, setSavingUpload] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editTemplateHtml, setEditTemplateHtml] = useState('');
  const [editTemplateName, setEditTemplateName] = useState('');
  const [editTemplateSubject, setEditTemplateSubject] = useState('');
  const [editPreviewTab, setEditPreviewTab] = useState('html');
  const [savingEdit, setSavingEdit] = useState(false);
  const [showDripModal, setShowDripModal] = useState(false);
  const [emailAddresses, setEmailAddresses] = useState('');
  const [dripEmails, setDripEmails] = useState([
    { templateId: '', dripDate: '' },
    { templateId: '', dripDate: '' },
    { templateId: '', dripDate: '' }
  ]);
  const [schedulingDrip, setSchedulingDrip] = useState(false);
  const [dripContacts, setDripContacts] = useState([]);
  const [loadingDripContacts, setLoadingDripContacts] = useState(false);
  const [showContactSelector, setShowContactSelector] = useState(false);
  const [showSenderModal, setShowSenderModal] = useState(false);
  const senderLocation = useLocation();
  useEffect(() => {
    if (new URLSearchParams(senderLocation.search).get('sender') === 'fix') setShowSenderModal(true);
  }, [senderLocation.search]);
  const [senderEmail, setSenderEmail] = useState('');
  const [senderPassword, setSenderPassword] = useState('');
  const [showAppPasswordHelp, setShowAppPasswordHelp] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [uploadedImages, setUploadedImages] = useState([]);
  const [showListingKeyModal, setShowListingKeyModal] = useState(false);
  const [listingKey, setListingKey] = useState('');
  const [loadingListingData, setLoadingListingData] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [generateRecipientName, setGenerateRecipientName] = useState('');
  const [generatingTemplate, setGeneratingTemplate] = useState(false);
  const [aiSubject, setAiSubject] = useState('');
  const [templateScope, setTemplateScope] = useState(defaultScope);
  const [uploadTemplateScope, setUploadTemplateScope] = useState(defaultScope);
  const [previewOwnership, setPreviewOwnership] = useState(null);
  const [editTemplateScope, setEditTemplateScope] = useState('brokerage');

  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureConfigured, setSignatureConfigured] = useState(false);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [signatureSaving, setSignatureSaving] = useState(false);
  const [signatureError, setSignatureError] = useState('');
  const [signatureForm, setSignatureForm] = useState(() => createSignatureForm(null, currentUser));
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  const getOwnership = (scope, createdByUserId = currentUserId) => ({
    scope,
    ownerUserId: scope === 'agent' ? currentUserId : null,
    createdByUserId
  });

  const selectTab = (tab) => {
    setActiveTab(tab);
    navigate(tab === 'create' ? '/admin/email-manager' : `/admin/email-manager?tab=${tab}`, { replace: true });
  };

  const appendOwnershipFields = (formData, ownership) => {
    formData.append('scope', ownership.scope);
    if (ownership.ownerUserId) {
      formData.append('ownerUserId', String(ownership.ownerUserId));
    }
    if (ownership.createdByUserId) {
      formData.append('createdByUserId', String(ownership.createdByUserId));
    }
  };

  const getApiError = async (response, fallback) => {
    try {
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await response.json();
        if (data.code === 'AGENT_PROFILE_NOT_FOUND') {
          return 'Your email profile is missing or inactive. Please contact an administrator.';
        }
        return data.message || fallback;
      }
      return (await response.text()) || fallback;
    } catch (error) {
      return fallback;
    }
  };

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';

    fetchUploadedImages();
    if (activeTab === 'templates') {
      fetchTemplates();
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps -- data refresh follows tab changes

  useEffect(() => {
    const fetchSenderConfig = async () => {
      try {
        const res = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/feature-flag?siteId=${SITE_CONFIG.SITE_ID}`, { headers: { 'X-API-Key': 'treas-api-key-2025' } });
        if (res.ok) {
          const data = await res.json();
          if (data.senderEmail) setSenderEmail(data.senderEmail);
          if (data.senderEmailPassword) {
            const pw = await decryptPassword(data.senderEmailPassword);
            if (pw) setSenderPassword(pw);
          }
        }
      } catch (e) {}
    };
    fetchSenderConfig();
  }, []);

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const params = new URLSearchParams({ siteId: SITE_CONFIG.SITE_ID });
      if (currentUserId) params.set('userId', String(currentUserId));
      const response = await fetch(`https://tophamtechbe.com/api/treas/comm/templates?${params}`, {
        headers: { 'X-API-Key': 'treas-api-key-2025' }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      showToast('Error loading templates', 'error');
    }
    setLoadingTemplates(false);
  };

  const fetchUploadedImages = async () => {
    try {
      const response = await fetch(`https://tophamtechbeb.com/api/toptech/onboarding/v1/get-all-email-images/${SITE_CONFIG.SITE_ID}`, {
        headers: { 'X-API-Key': 'treas-api-key-2025' }
      });

      if (response.ok) {
        const data = await response.json();
        setUploadedImages(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const handleEditorChange = () => {
    if (editorRef.current) {
      setEmailContent(editorRef.current.innerHTML);
    }
  };

  const formatText = (command, value = null) => {
    document.execCommand(command, false, value);
    handleEditorChange();
  };

  const insertImage = () => {
    fileInputRef.current?.click();
  };

  const handleImageDragStart = (e, imageUrl) => {
    e.dataTransfer.setData('text/html', `<img src="${imageUrl}" style="max-width: 100%; height: auto; margin: 10px 0;" alt="Email Image" />`);
  };

  const handleEditorDrop = (e) => {
    e.preventDefault();
    const html = e.dataTransfer.getData('text/html');
    if (html) {
      document.execCommand('insertHTML', false, html);
      handleEditorChange();
    }
  };

  const handleEditorDragOver = (e) => {
    e.preventDefault();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      showToast('Uploading image...', 'success');

      const formData = new FormData();
      formData.append('email_image', file);
      formData.append('site_id', SITE_CONFIG.SITE_ID);

      try {
        const response = await fetch('https://tophamtechbeb.com/api/toptech/onboarding/v1/upload-email-image', {
          method: 'POST',
          headers: {
            'X-API-Key': 'treas-api-key-2025'
          },
          body: formData
        });

        if (response.ok) {
          fetchUploadedImages();
          showToast('Image uploaded successfully!', 'success');
        } else {
          showToast('Error uploading image. Please try again.', 'error');
        }
      } catch (error) {
        showToast('Network error uploading image.', 'error');
      }
    }
  };

  const generatePreview = async () => {
    if (templateScope === 'agent' && !currentUserId) {
      showToast('Your user account is missing a valid ID. Please contact an administrator.', 'error');
      return;
    }
    setLoadingPreview(true);
    setAiSubject('');
    const htmlTemplate = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${templateName || 'Email Template'}</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
            background-color: #f4f4f4;
        }
        .email-container {
            background: white;
            padding: 30px;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        .header {
            text-align: center;
            border-bottom: 2px solid #0176d3;
            padding-bottom: 20px;
            margin-bottom: 30px;
        }
        .logo {
            font-size: 24px;
            font-weight: bold;
            color: #032d60;
        }
        .content {
            margin-bottom: 30px;
        }
        .footer {
            text-align: center;
            font-size: 12px;
            color: #666;
            border-top: 1px solid #eee;
            padding-top: 20px;
        }
        img {
            max-width: 100%;
            height: auto;
            border-radius: 4px;
        }
        a {
            color: #0176d3;
            text-decoration: none;
        }
        .cta-button {
            display: inline-block;
            background: #0176d3;
            color: white;
            padding: 12px 24px;
            border-radius: 6px;
            text-decoration: none;
            font-weight: bold;
            margin: 20px 0;
        }
    </style>
</head>
<body>
    <div class="email-container">
        <div class="header">
            <div class="logo">Real Estate Services</div>
        </div>
        <div class="content">
            ${emailContent}
        </div>
        <div class="footer">
            <p>© 2025 Real Estate Services. All rights reserved.</p>
            <p>Professional Real Estate Services</p>
        </div>
    </div>
</body>
</html>`;

    // Check character count - skip chat call if over 200,000 characters
    if (htmlTemplate.length > 200000) {
      setImprovedHtml(htmlTemplate);
      showToast('Template too large for enhancement, showing original', 'warning');
    } else {
      try {
        const response = await fetch('https://tophamtechbe.com/llm/v1/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: `make this html template more professional: ${htmlTemplate}`,
            site_id: SITE_CONFIG.SITE_ID
          })
        });

        if (response.ok) {
          const data = await response.json();
          const llmResponse = data.response || data.message || htmlTemplate;

          const htmlMatch = llmResponse.match(/<!DOCTYPE html[\s\S]*?<\/html>/i);
          const extractedHtml = htmlMatch ? htmlMatch[0] : llmResponse;

          setImprovedHtml(extractedHtml);
        } else {
          setImprovedHtml(htmlTemplate);
          showToast('Could not improve template, showing original', 'warning');
        }
      } catch (error) {
        setImprovedHtml(htmlTemplate);
        showToast('Error improving template, showing original', 'error');
      }
    }

    setLoadingPreview(false);
    setPreviewOwnership(getOwnership(templateScope));
    setShowPreviewModal(true);
  };

  const copyHtml = (html = improvedHtml) => {
    navigator.clipboard.writeText(html);
    showToast('HTML copied to clipboard!', 'success');
  };

  const downloadHtml = (html = improvedHtml) => {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${templateName || 'email-template'}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast('HTML file downloaded!', 'success');
  };

  const saveTemplate = async () => {
    const ownership = previewOwnership || getOwnership(templateScope);
    if (ownership.scope === 'agent' && !ownership.ownerUserId) {
      showToast('Your user account is missing a valid ID. Please contact an administrator.', 'error');
      return;
    }
    setSavingTemplate(true);

    const formData = new FormData();
    formData.append('templateName', templateName);
    formData.append('subject', aiSubject || templateName);
    formData.append('siteId', SITE_CONFIG.SITE_ID);
    formData.append('body', improvedHtml);
    appendOwnershipFields(formData, ownership);

    try {
      const response = await fetch('https://tophamtechbe.com/api/treas/comm/store-template', {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        },
        body: formData
      });

      if (response.ok) {
        showToast('Template saved successfully!', 'success');
        setShowPreviewModal(false);
        setAiSubject('');
        setPreviewOwnership(null);
        selectTab('templates');
      } else {
        showToast(await getApiError(response, 'Error saving template. Please try again.'), 'error');
      }
    } catch (error) {
      showToast('Network error saving template.', 'error');
    }

    setSavingTemplate(false);
  };

  const saveUploadTemplate = async () => {
    if (uploadTemplateScope === 'agent' && !currentUserId) {
      showToast('Your user account is missing a valid ID. Please contact an administrator.', 'error');
      return;
    }
    setSavingUpload(true);

    const formData = new FormData();
    formData.append('templateName', uploadTemplateName);
    formData.append('subject', uploadTemplateName);
    formData.append('siteId', SITE_CONFIG.SITE_ID);
    formData.append('body', uploadHtml);
    appendOwnershipFields(formData, getOwnership(uploadTemplateScope));

    try {
      const response = await fetch('https://tophamtechbe.com/api/treas/comm/store-template', {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        },
        body: formData
      });

      if (response.ok) {
        showToast('Template saved successfully!', 'success');
        setUploadTemplateName('');
        setUploadHtml('');
        selectTab('templates');
      } else {
        showToast(await getApiError(response, 'Error saving template. Please try again.'), 'error');
      }
    } catch (error) {
      showToast('Network error saving template.', 'error');
    }

    setSavingUpload(false);
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setEditTemplateHtml(template.body);
    setEditTemplateName(template.templateName);
    setEditTemplateSubject(template.subject);
    setEditTemplateScope(template.scope === 'agent' ? 'agent' : 'brokerage');
  };

  const saveEditedTemplate = async () => {
    if (editTemplateScope === 'agent' && !currentUserId) {
      showToast('Your user account is missing a valid ID. Please contact an administrator.', 'error');
      return;
    }
    setSavingEdit(true);
    const originalScope = editingTemplate.scope === 'agent' ? 'agent' : 'brokerage';
    const scopeChanged = originalScope !== editTemplateScope;
    const createdByUserId = scopeChanged
      ? currentUserId
      : (getPositiveUserId({ id: editingTemplate.createdByUserId }) || currentUserId);
    const ownership = getOwnership(editTemplateScope, createdByUserId);
    const formData = new FormData();
    formData.append('templateName', editTemplateName);
    formData.append('subject', editTemplateSubject);
    formData.append('siteId', editingTemplate.siteId);
    formData.append('body', editTemplateHtml);
    appendOwnershipFields(formData, ownership);

    try {
      const response = await fetch('https://tophamtechbe.com/api/treas/comm/store-template', {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        },
        body: formData
      });

      if (response.ok) {
        if (scopeChanged) {
          const deleteResponse = await fetch(`https://tophamtechbe.com/api/treas/comm/templates/${editingTemplate.id}`, {
            method: 'DELETE',
            headers: { 'X-API-Key': 'treas-api-key-2025' }
          });
          if (!deleteResponse.ok) {
            showToast('The replacement was saved, but the original could not be deleted. Please try saving again.', 'warning');
            fetchTemplates();
            setSavingEdit(false);
            return;
          }
        }
        showToast('Template updated successfully!', 'success');
        setEditingTemplate(null);
        fetchTemplates();
      } else {
        showToast(await getApiError(response, 'Error updating template. Please try again.'), 'error');
      }
    } catch (error) {
      showToast('Network error updating template.', 'error');
    }

    setSavingEdit(false);
  };

  const deleteTemplate = async (templateId) => {
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/comm/templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'X-API-Key': 'treas-api-key-2025' }
      });

      if (response.ok) {
        showToast('Template deleted successfully!', 'success');
        fetchTemplates();
      } else {
        showToast('Error deleting template.', 'error');
      }
    } catch (error) {
      showToast('Network error deleting template.', 'error');
    }
  };

  const handleDripChange = (index, field, value) => {
    const newDripEmails = [...dripEmails];
    newDripEmails[index][field] = value;
    setDripEmails(newDripEmails);
  };

  const scheduleDrip = async () => {
    setSchedulingDrip(true);

    const formData = new FormData();
    formData.append('emailaddresses', emailAddresses);
    formData.append('siteId', SITE_CONFIG.SITE_ID);

    if (senderEmail) {
      formData.append('email', senderEmail);
    }
    if (senderPassword) {
      formData.append('emailPassword', senderPassword);
    }

    dripEmails.forEach((email, index) => {
      if (email.templateId && email.dripDate) {
        formData.append(`templateId${index + 1}`, email.templateId);
        formData.append(`dripDate${index + 1}`, email.dripDate);
      }
    });

    try {
      const response = await fetch('https://tophamtechbe.com/api/treas/comm/schedule-drip', {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        },
        body: formData
      });

      if (response.ok) {
        showToast('Drip campaign scheduled successfully!', 'success');
        setShowDripModal(false);
        setEmailAddresses('');
        setDripEmails([
          { templateId: '', dripDate: '' },
          { templateId: '', dripDate: '' },
          { templateId: '', dripDate: '' }
        ]);
      } else {
        showToast('Error scheduling drip campaign.', 'error');
      }
    } catch (error) {
      showToast('Network error scheduling drip campaign.', 'error');
    }

    setSchedulingDrip(false);
  };

  const fetchDripContacts = async () => {
    setLoadingDripContacts(true);
    try {
      const response = await fetch(`https://tophamtechbe.com/treas-postgrid/s3/get-address-list/${SITE_CONFIG.SITE_ID}`, {
        headers: { 'X-API-Key': 'treas-api-key-2025' }
      });

      if (response.ok) {
        const data = await response.json();
        setDripContacts(data.contacts || []);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
    setLoadingDripContacts(false);
  };

  const addContactToEmails = (contact) => {
    if (contact.Email) {
      const currentEmails = emailAddresses.split(',').map(e => e.trim()).filter(e => e);
      if (!currentEmails.includes(contact.Email)) {
        const newEmails = [...currentEmails, contact.Email].join(', ');
        setEmailAddresses(newEmails);
      }
    }
  };

  const deleteImage = async (fileId) => {
    try {
      const response = await fetch(`https://tophamtechbeb.com/api/toptech/onboarding/v1/delete-email-image/${SITE_CONFIG.SITE_ID}/${fileId}`, {
        method: 'DELETE',
        headers: { 'X-API-Key': 'treas-api-key-2025' }
      });

      if (response.ok) {
        fetchUploadedImages();
        showToast('Image deleted successfully!', 'success');
      } else {
        showToast('Error deleting image.', 'error');
      }
    } catch (error) {
      showToast('Network error deleting image.', 'error');
    }
  };

  const createFromListingKey = async () => {
    if (!listingKey.trim()) {
      showToast('Please enter a listing key', 'error');
      return;
    }

    setLoadingListingData(true);

    try {
      // Get property summary
      const summaryResponse = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?listingKey=${listingKey}`, {});

      if (!summaryResponse.ok) {
        throw new Error('Failed to fetch property summary');
      }

      const summaryData = await summaryResponse.json();

      if (!summaryData.properties || summaryData.properties.length === 0) {
        throw new Error('No property found with this listing key');
      }

      // Get property details
      const detailsResponse = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-data?listingKey=${listingKey}`, {});

      if (!detailsResponse.ok) {
        throw new Error('Failed to fetch property details');
      }

      const detailsData = await detailsResponse.json();

      // Generate text content from the data
      const property = detailsData.properties[0];
      const location = property.location;
      const features = property.features;

      const textContent = `Featured Property - ${formatStreetAddress(location)}

View Full Listing: ${window.location.origin}/listing/${listingKey}

Property Details:
Address: ${formatPropertyAddress(location)}
Price: $${property.current_price?.toLocaleString() || 'Contact for pricing'}
Type: ${property.property_sub_type || property.property_type}
Living Area: ${property.living_area ? property.living_area.toLocaleString() + ' sq ft' : 'N/A'}
Bedrooms: ${property.bedrooms_total || 'N/A'}
Bathrooms: ${property.bathrooms_full || 0} full${property.bathrooms_half ? ', ' + property.bathrooms_half + ' half' : ''}
Year Built: ${property.year_built || 'N/A'}
${property.stories ? `Stories: ${property.stories}
` : ''}${features?.garage_spaces ? `Garage: ${features.garage_spaces} spaces
` : ''}
${features?.appliances ? `
Appliances & Features:
${features.appliances.split(',').join(', ')}
` : ''}${features?.interior_features ? `
Interior Features:
${features.interior_features.split(',').join(', ')}
` : ''}${features?.exterior_features ? `
Exterior Features:
${features.exterior_features.split(',').join(', ')}
` : ''}${property.schools?.elementary_school ? `
School Information:
Elementary: ${property.schools.elementary_school}${property.schools.middle_school ? `
Middle: ${property.schools.middle_school}` : ''}${property.schools.high_school ? `
High: ${property.schools.high_school}` : ''}
` : ''}
Interested in this property? Contact us today!

MLS #${property.listing_key} | Status: ${property.standard_status}`;

      // Set initial text content
      if (editorRef.current) {
        editorRef.current.innerHTML = textContent.replace(/\n/g, '<br>');
        setEmailContent(textContent.replace(/\n/g, '<br>'));
      }

      // Fetch property media
      let imageUrl = '';
      try {
        const mediaRes = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${listingKey}`, {});
        if (mediaRes.ok) {
          const mediaData = await mediaRes.json();
          if (mediaData.media && mediaData.media.length > 0) {
            const main = mediaData.media.sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
            imageUrl = main.media_url || main.s3_link;
          }
        }
      } catch (e) {}

      // Enhance with chat call
      try {
        const listingUrl = `${window.location.origin}/listing/${listingKey}`;
        const response = await fetch('https://tophamtechbe.com/llm/v1/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: `compose a professional html template we can use in an email, place this url in a link at the top of the email ${listingUrl}${imageUrl ? `. Use this exact image url as an img src: ${imageUrl}` : ''}, write this in easy to read paragraph format less than 4 sentences: ${textContent}`,
            site_id: SITE_CONFIG.SITE_ID,
            model: 'gpt-4o'
          })
        });

        if (response.ok) {
          const data = await response.json();
          let enhancedHtml = data.response || data.message || textContent.replace(/\n/g, '<br>');

          // Clean up chat response - extract HTML content
          const htmlMatch = enhancedHtml.match(/```html\s*([\s\S]*?)\s*```/);
          if (htmlMatch) {
            enhancedHtml = htmlMatch[1].trim();
          } else {
            // Remove common chat prefixes
            enhancedHtml = enhancedHtml.replace(/^.*?(?:Here is the template:|Here's the template:|template:)\s*```?html?\s*/i, '')
                                     .replace(/```\s*$/i, '')
                                     .replace(/^Sure,.*?template:\s*/i, '')
                                     .trim();
          }

          if (editorRef.current) {
            editorRef.current.innerHTML = enhancedHtml;
            setEmailContent(enhancedHtml);
          }
        }
      } catch (error) {
        console.error('Error enhancing content:', error);
      }

      // Set template name
      setTemplateName(`Property Listing - ${formatStreetAddress(location)}`);


      setShowListingKeyModal(false);
      setListingKey('');
      showToast('Property data loaded successfully!', 'success');

    } catch (error) {
      console.error('Error fetching listing data:', error);
      showToast(error.message || 'Error loading property data', 'error');
    }

    setLoadingListingData(false);
  };

  const generateAiTemplate = async () => {
    if (!generatePrompt.trim()) {
      showToast('Please describe the email you want', 'error');
      return;
    }

    setGeneratingTemplate(true);

    try {
      if (templateScope === 'agent' && !currentUserId) {
        throw new Error('Your user account is missing a valid ID. Please contact an administrator.');
      }
      const body = {
        siteId: SITE_CONFIG.SITE_ID,
        request: generatePrompt,
        generationMode: 'agent_marketing',
        researchMode: 'auto'
      };
      if (templateScope === 'agent') body.userId = currentUserId;
      if (generateRecipientName.trim()) {
        body.recipientName = generateRecipientName.trim();
      }

      const response = await fetch('https://tophamtechbe.com/api/treas/comm/generate-ai-template', {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(await getApiError(response, 'Failed to generate template'));
      }

      const data = await response.json();

      if (!data.html) {
        throw new Error('No HTML returned from generator');
      }

      setImprovedHtml(data.html);
      setAiSubject(data.subject || '');
      setPreviewOwnership(getOwnership(templateScope));
      if (!templateName.trim() && data.subject) {
        setTemplateName(data.subject);
      }
      setPreviewTab('preview');
      setShowGenerateModal(false);
      setGeneratePrompt('');
      setGenerateRecipientName('');
      setShowPreviewModal(true);
      showToast('Template generated successfully!', 'success');
    } catch (error) {
      console.error('Error generating AI template:', error);
      showToast(error.message || 'Error generating template', 'error');
    }

    setGeneratingTemplate(false);
  };

  const openSendEmailModal = (template) => {
    setSelectedTemplate(template);
    setShowSendEmailModal(true);
  };

  const sendEmail = async () => {
    if (!recipientEmail.trim()) {
      showToast('Please enter recipient email', 'error');
      return;
    }

    setSendingEmail(true);

    const formData = new FormData();
    formData.append('emailaddress', recipientEmail);
    formData.append('title', selectedTemplate.subject || selectedTemplate.templateName);
    formData.append('body', selectedTemplate.body);
    formData.append('template', selectedTemplate.templateName);

    if (senderEmail) {
      formData.append('email', senderEmail);
    }
    if (senderPassword) {
      formData.append('emailPassword', senderPassword);
    }

    try {
      const response = await sendEmailRequest('https://tophamtechbe.com/api/treas/comm/send-email', {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        },
        body: formData
      });

      if (response.ok) {
        showToast('Email sent successfully!', 'success');
        setShowSendEmailModal(false);
        setRecipientEmail('');
      } else {
        showToast('Email delivery was not fully confirmed. Check delivery status before resending.', 'error');
      }
    } catch (error) {
      showToast('Network error sending email.', 'error');
    }

    setSendingEmail(false);
  };

  const openSignatureSetup = async () => {
    setShowSignatureModal(true);
    setSignatureLoading(true);
    setSignatureError('');

    try {
      const data = await getCustomSignature(currentUser.token);
      setSignatureConfigured(data.configured);
      setSignatureForm(createSignatureForm(data.profile, currentUser));
    } catch (signatureLoadError) {
      setSignatureError(signatureLoadError.message || 'Your email signature could not be loaded.');
    } finally {
      setSignatureLoading(false);
    }
  };

  const updateSignatureField = (field, value) => {
    setSignatureForm(current => ({ ...current, [field]: value }));
    setSignatureError('');
  };

  const updateSignatureDocument = (key, value) => {
    setSignatureForm(current => ({
      ...current,
      documentUrls: { ...current.documentUrls, [key]: value }
    }));
    setSignatureError('');
  };

  const handleSaveSignature = async (event) => {
    event.preventDefault();
    const name = signatureForm.name.trim();
    const email = signatureForm.email.trim();
    const brokerage = signatureForm.brokerage.trim();

    if (!name || !email || !brokerage) {
      setSignatureError('Name, email, and brokerage are required.');
      return;
    }
    if (!isValidEmailAddress(email)) {
      setSignatureError('Enter a valid email address.');
      return;
    }

    const invalidDocument = SIGNATURE_DOCUMENTS.find(document => {
      const url = signatureForm.documentUrls[document.key]?.trim();
      return url && !isValidHttpsUrl(url);
    });
    if (invalidDocument) {
      setSignatureError(`${invalidDocument.label} must use a valid HTTPS URL.`);
      return;
    }

    const documents = SIGNATURE_DOCUMENTS.flatMap(document => {
      const url = signatureForm.documentUrls[document.key]?.trim();
      return url ? [{ ...document, url }] : [];
    });
    const payload = {
      name,
      phone: signatureForm.phone.trim(),
      email,
      city: signatureForm.city.trim(),
      state: signatureForm.state.trim(),
      brokerage,
      documents
    };

    setSignatureSaving(true);
    setSignatureError('');
    try {
      const data = await saveCustomSignature(currentUser.token, payload);
      setSignatureConfigured(data.configured);
      setSignatureForm(createSignatureForm(data.profile, currentUser));
      setShowSignatureModal(false);
      showToast('Email signature saved successfully!', 'success');
    } catch (signatureSaveError) {
      setSignatureError(signatureSaveError.message || 'Your email signature could not be saved.');
    } finally {
      setSignatureSaving(false);
    }
  };

  return (
    <div className="email-manager">
      <SenderWarningBanner onFix={() => setShowSenderModal(true)} />
      <div className="email-header">
        <div className="email-header-brand">
          <span className="email-header-mark"><EmailUiIcon name="mail" /></span>
          <div>
            <span className="email-eyebrow">Communication workspace</span>
            <h1>Email Manager</h1>
            <p>Create polished content, manage your library, and launch thoughtful follow-up.</p>
          </div>
        </div>
        <div className="email-header-actions">
          {canManageSignature && (
            <button type="button" onClick={openSignatureSetup} className="signature-setup-btn">
              <EmailUiIcon name="signature" />
              {signatureConfigured ? 'Edit signature' : 'Set up signature'}
            </button>
          )}
          <button onClick={() => navigate('/admin')} className="back-button">
            <EmailUiIcon name="dashboard" />
            Back to dashboard
          </button>
        </div>
      </div>

      <div className={`email-content ${activeTab === 'automations' ? 'automation-active' : ''}`}>
        <div className="email-tabs">
          <button
            className={`tab-button ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => selectTab('create')}
          >
            <EmailUiIcon name="compose" />
            <span>Create</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'upload' ? 'active' : ''}`}
            onClick={() => selectTab('upload')}
          >
            <EmailUiIcon name="upload" />
            <span>Upload</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'templates' ? 'active' : ''}`}
            onClick={() => selectTab('templates')}
          >
            <EmailUiIcon name="library" />
            <span>Templates</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'automations' ? 'active' : ''}`}
            onClick={() => selectTab('automations')}
          >
            <EmailUiIcon name="automation" />
            <span>Automations</span>
          </button>
        </div>

        {activeTab === 'create' && (
          <div className="email-form">
          <div className="email-section-intro">
            <span className="email-section-icon"><EmailUiIcon name="compose" /></span>
            <div>
              <span className="email-eyebrow">Template studio</span>
              <h2>Design a professional email</h2>
              <p>Build from scratch, use your media library, or let AI create a polished first draft.</p>
            </div>
          </div>
          <div className="subject-section">
            <label>Template Name</label>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Enter template name..."
              className="subject-input"
            />
          </div>

          <div className="scope-section">
            <label>Template Access</label>
            <TemplateScopeSelector
              name="create-template-scope"
              value={templateScope}
              onChange={setTemplateScope}
              personalDisabled={!currentUserId}
            />
            <p className="scope-help">AI-generated personal templates use your active agent profile and signature. Manual content keeps the HTML you provide.</p>
          </div>

          <div className="image-section">
            <div className="image-header">
              <label>Images</label>
              <div className="image-buttons">
                <button type="button" onClick={insertImage} className="upload-btn">
                  <EmailUiIcon name="image" /> Upload image
                </button>
                <button type="button" onClick={() => setShowListingKeyModal(true)} className="listing-key-btn">
                  <EmailUiIcon name="home" /> From listing
                </button>
                <button type="button" onClick={() => setShowGenerateModal(true)} className="generate-ai-btn">
                  <EmailUiIcon name="sparkle" /> Generate with AI
                </button>
              </div>
            </div>

            <div className="image-gallery">
              {uploadedImages.map((image, index) => (
                <div
                  key={index}
                  className="image-item"
                  draggable
                  onDragStart={(e) => handleImageDragStart(e, image.email_image_url)}
                  title="Drag to email content"
                >
                  <button
                    className="delete-image-btn"
                    onClick={() => deleteImage(image.file_id)}
                    title="Delete image"
                  >
                    ×
                  </button>
                  <img
                    src={image.email_image_url}
                    alt={`Upload ${index + 1}`}
                    onError={(e) => {
                      console.log('Image failed to load, refreshing...');
                      fetchUploadedImages();
                    }}
                  />
                </div>
              ))}
              {uploadedImages.length === 0 && (
                <div className="no-images">No images uploaded yet</div>
              )}
            </div>
          </div>

          <div className="editor-section">
            <label>Email Content</label>

            <div className="toolbar">
              <button onClick={() => formatText('bold')} className="tool-btn">
                <strong>B</strong>
              </button>
              <button onClick={() => formatText('italic')} className="tool-btn">
                <em>I</em>
              </button>
              <button onClick={() => formatText('underline')} className="tool-btn">
                <u>U</u>
              </button>
              <div className="separator"></div>
              <button onClick={() => formatText('justifyLeft')} className="tool-btn">
                ←
              </button>
              <button onClick={() => formatText('justifyCenter')} className="tool-btn">
                ↔
              </button>
              <button onClick={() => formatText('justifyRight')} className="tool-btn">
                →
              </button>
              <div className="separator"></div>
              <button onClick={() => formatText('insertUnorderedList')} className="tool-btn">
                • List
              </button>
              <button onClick={() => formatText('insertOrderedList')} className="tool-btn">
                1. List
              </button>
              <div className="separator"></div>
              <button onClick={() => formatText('createLink', prompt('Enter URL:'))} className="tool-btn">
                🔗 Link
              </button>
              <div className="separator"></div>
              <select onChange={(e) => formatText('fontSize', e.target.value)} className="font-select">
                <option value="">Font Size</option>
                <option value="1">Small</option>
                <option value="3">Normal</option>
                <option value="5">Large</option>
                <option value="7">Extra Large</option>
              </select>
              <input
                type="color"
                onChange={(e) => formatText('foreColor', e.target.value)}
                className="color-picker"
                title="Text Color"
              />
            </div>

            <div
              ref={editorRef}
              className="editor"
              contentEditable
              onInput={handleEditorChange}
              onDrop={handleEditorDrop}
              onDragOver={handleEditorDragOver}
              data-placeholder="Start designing your professional email here..."
            />

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />
          </div>

          <div className="actions">
            <button onClick={generatePreview} disabled={loadingPreview || !templateName.trim()} className="generate-btn">
              <EmailUiIcon name="send" />
              {loadingPreview ? 'Generating preview…' : 'Preview template'}
            </button>
          </div>
        </div>
        )}

        {activeTab === 'upload' && (
          <div className="upload-section">
            <div className="email-section-intro">
              <span className="email-section-icon upload"><EmailUiIcon name="code" /></span>
              <div>
                <span className="email-eyebrow">HTML import</span>
                <h2>Bring your own template</h2>
                <p>Paste production-ready HTML, review it instantly, and save it to your shared library.</p>
              </div>
            </div>
            <div className="upload-form">
              <div className="upload-name-section">
                <label>Template Name</label>
                <input
                  type="text"
                  value={uploadTemplateName}
                  onChange={(e) => setUploadTemplateName(e.target.value)}
                  placeholder="Enter template name..."
                  className="upload-name-input"
                />
              </div>

              <div className="scope-section">
                <label>Template Access</label>
                <TemplateScopeSelector
                  name="upload-template-scope"
                  value={uploadTemplateScope}
                  onChange={setUploadTemplateScope}
                  personalDisabled={!currentUserId}
                />
                <p className="scope-help">Uploaded HTML is saved exactly as supplied; add any desired signature to the HTML before uploading.</p>
              </div>

              <div className="upload-editor-section">
                <div className="upload-tabs">
                  <button
                    className={`upload-tab ${uploadPreviewTab === 'html' ? 'active' : ''}`}
                    onClick={() => setUploadPreviewTab('html')}
                  >
                    Edit HTML
                  </button>
                  <button
                    className={`upload-tab ${uploadPreviewTab === 'preview' ? 'active' : ''}`}
                    onClick={() => setUploadPreviewTab('preview')}
                  >
                    Preview
                  </button>
                </div>

                <div className="upload-content">
                  {uploadPreviewTab === 'html' ? (
                    <textarea
                      value={uploadHtml}
                      onChange={(e) => setUploadHtml(e.target.value)}
                      className="upload-textarea"
                      rows="20"
                      placeholder="Paste your HTML template here..."
                    />
                  ) : (
                    <iframe
                      srcDoc={uploadHtml}
                      className="upload-preview-frame"
                      title="Template Preview"
                    />
                  )}
                </div>
              </div>

              <div className="upload-actions">
                <button
                  onClick={saveUploadTemplate}
                  disabled={savingUpload || !uploadTemplateName.trim() || !uploadHtml.trim()}
                  className="upload-save-btn"
                >
                  {savingUpload ? 'Saving...' : 'Save Template'}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="templates-section">
            <div className="templates-header">
              <div className="templates-title">
                <span className="email-eyebrow">Content library</span>
                <div><h2>Email templates</h2><span className="template-count">{templates.length}</span></div>
                <p>Preview, edit, send, and organize every reusable email in one place.</p>
              </div>
              <div className="templates-actions">
                <button onClick={() => setShowDripModal(true)} className="drip-btn">
                  <EmailUiIcon name="calendar" /> Schedule drip
                </button>
                <button onClick={() => setShowSenderModal(true)} className="sender-email-btn">
                  <EmailUiIcon name="mail" /> Sender email
                </button>
                <button onClick={fetchTemplates} className="refresh-btn">
                  <EmailUiIcon name="refresh" /> Refresh
                </button>
              </div>
            </div>

            {loadingTemplates ? (
              <div className="loading">Loading templates...</div>
            ) : templates.length > 0 ? (
              <div className="templates-grid">
                {templates.map((template, index) => (
                  <div key={index} className="template-card">
                    <div className="template-header">
                      <h4>{template.templateName || template.name}</h4>
                      <div className="template-meta">
                        <span className={`template-scope-badge ${template.scope === 'agent' ? 'personal' : 'brokerage'}`}>
                          {template.scope === 'agent' ? 'Personal' : 'Brokerage-wide'}
                        </span>
                        <span className="template-date">
                          {new Date(template.created_at || template.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="template-subject">
                      Subject: {template.subject}
                    </div>
                    <div className="template-preview">
                      <iframe
                        srcDoc={template.body}
                        className="template-preview-frame"
                        title={`Preview ${template.templateName || template.name}`}
                      />
                    </div>
                    <div className="template-actions">
                      <button
                        onClick={() => navigator.clipboard.writeText(template.body)}
                        className="template-btn"
                      >
                        Copy HTML
                      </button>
                      <button
                        onClick={() => openEditTemplate(template)}
                        className="template-btn"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => openSendEmailModal(template)}
                        className="send-template-btn"
                      >
                        Send
                      </button>
                      <button
                        onClick={() => deleteTemplate(template.id)}
                        className="delete-template-btn"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-templates">
                No templates found. Create or upload templates to see them here.
              </div>
            )}
          </div>
        )}

        {activeTab === 'automations' && <ChatLeadCampaignBuilder senderPassword={senderPassword} />}
      </div>

      {showPreviewModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Template Preview</h3>
              <button onClick={() => setShowPreviewModal(false)} className="close-btn">
                ×
              </button>
            </div>
            <div className="preview-scope-summary">
              Saving as <strong>{previewOwnership?.scope === 'agent' ? 'Personal' : 'Brokerage-wide'}</strong>
            </div>
            <div className="preview-tabs">
              <button
                className={`preview-tab ${previewTab === 'preview' ? 'active' : ''}`}
                onClick={() => setPreviewTab('preview')}
              >
                Improved Preview
              </button>
              <button
                className={`preview-tab ${previewTab === 'html' ? 'active' : ''}`}
                onClick={() => setPreviewTab('html')}
              >
                View HTML
              </button>
            </div>
            <div className="html-preview">
              {previewTab === 'preview' ? (
                <iframe
                  srcDoc={improvedHtml}
                  className="preview-frame"
                  title="Email Preview"
                />
              ) : (
                <textarea
                  value={improvedHtml}
                  onChange={(e) => setImprovedHtml(e.target.value)}
                  className="html-textarea"
                  rows="20"
                  placeholder="Edit HTML here..."
                />
              )}
            </div>
            <div className="modal-actions">
              <button onClick={() => copyHtml(improvedHtml)} className="copy-btn">
                Copy HTML
              </button>
              <button onClick={() => downloadHtml(improvedHtml)} className="download-btn">
                Download HTML
              </button>
              <button onClick={saveTemplate} disabled={savingTemplate} className="save-btn">
                {savingTemplate ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingTemplate && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Template</h3>
              <button onClick={() => setEditingTemplate(null)} className="close-btn">
                ×
              </button>
            </div>

            <div className="edit-form">
              <div className="edit-fields">
                <div className="edit-field">
                  <label>Template Name</label>
                  <input
                    type="text"
                    value={editTemplateName}
                    onChange={(e) => setEditTemplateName(e.target.value)}
                    className="edit-input"
                  />
                </div>
                <div className="edit-field">
                  <label>Subject</label>
                  <input
                    type="text"
                    value={editTemplateSubject}
                    onChange={(e) => setEditTemplateSubject(e.target.value)}
                    className="edit-input"
                  />
                </div>
              </div>
              <div className="edit-field edit-scope-field">
                <label>Template Access</label>
                <TemplateScopeSelector
                  name="edit-template-scope"
                  value={editTemplateScope}
                  onChange={setEditTemplateScope}
                  personalDisabled={!currentUserId}
                />
                {(editingTemplate.scope === 'agent' ? 'agent' : 'brokerage') !== editTemplateScope && (
                  <p className="scope-conversion-note">The replacement will be created first. After it saves successfully, the original template will be deleted.</p>
                )}
              </div>

              <div className="edit-tabs">
                <button
                  className={`edit-tab ${editPreviewTab === 'html' ? 'active' : ''}`}
                  onClick={() => setEditPreviewTab('html')}
                >
                  Edit HTML
                </button>
                <button
                  className={`edit-tab ${editPreviewTab === 'preview' ? 'active' : ''}`}
                  onClick={() => setEditPreviewTab('preview')}
                >
                  Preview
                </button>
              </div>

              <div className="edit-content">
                {editPreviewTab === 'html' ? (
                  <textarea
                    value={editTemplateHtml}
                    onChange={(e) => setEditTemplateHtml(e.target.value)}
                    className="edit-textarea"
                    rows="20"
                    placeholder="Edit HTML here..."
                  />
                ) : (
                  <iframe
                    srcDoc={editTemplateHtml}
                    className="edit-preview-frame"
                    title="Template Preview"
                  />
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button
                onClick={() => setEditingTemplate(null)}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={saveEditedTemplate}
                disabled={savingEdit}
                className="save-btn"
              >
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDripModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Schedule Drip Campaign</h3>
              <button onClick={() => setShowDripModal(false)} className="close-btn">
                ×
              </button>
            </div>

            <div className="drip-form">
              <div className="form-field">
                <div className="email-header">
                  <label>Email Addresses (comma separated)</label>
                  <div className="email-buttons">
                    <button
                      type="button"
                      onClick={() => {
                        setShowContactSelector(!showContactSelector);
                        if (!showContactSelector && dripContacts.length === 0) {
                          fetchDripContacts();
                        }
                      }}
                      className="contact-selector-btn"
                    >
                      {showContactSelector ? 'Hide Contacts' : 'Select from Contacts'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowSenderModal(true)}
                      className="sender-email-btn"
                    >
                      Sender Email
                    </button>
                  </div>
                </div>
                <textarea
                  value={emailAddresses}
                  onChange={(e) => setEmailAddresses(e.target.value)}
                  className="email-textarea"
                  rows="3"
                  placeholder="user1@email.com, user2@email.com, user3@email.com"
                />

                {showContactSelector && (
                  <div className="contact-selector">
                    {loadingDripContacts ? (
                      <div className="loading-contacts">Loading contacts...</div>
                    ) : dripContacts.length > 0 ? (
                      <>
                        <div className="contact-selector-header">
                          <button
                            type="button"
                            onClick={() => {
                              const allEmails = dripContacts
                                .filter(contact => contact.Email && contact.Email.trim())
                                .map(contact => contact.Email)
                                .join(', ');
                              setEmailAddresses(allEmails);
                            }}
                            className="add-all-btn"
                          >
                            Add All ({dripContacts.filter(contact => contact.Email && contact.Email.trim()).length})
                          </button>
                        </div>
                        <div className="contact-list">
                          {dripContacts.filter(contact => contact.Email && contact.Email.trim()).map((contact, index) => (
                            <div key={index} className="contact-item">
                              <div className="contact-info">
                                <div className="contact-name">{contact.Name}</div>
                                <div className="contact-email">{contact.Email}</div>
                              </div>
                              <button
                                type="button"
                                onClick={() => addContactToEmails(contact)}
                                className="add-contact-btn"
                              >
                                Add
                              </button>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="no-contacts-found">No contacts with email addresses found.</div>
                    )}
                  </div>
                )}
              </div>

              <div className="drip-schedule">
                <h4>Email Schedule (up to 3 emails)</h4>
                {dripEmails.map((email, index) => (
                  <div key={index} className="drip-row">
                    <div className="drip-field">
                      <label>Email {index + 1} Template</label>
                      <select
                        value={email.templateId}
                        onChange={(e) => handleDripChange(index, 'templateId', e.target.value)}
                        className="template-select"
                      >
                        <option value="">Select Template</option>
                        {templates.map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.templateName} · {template.scope === 'agent' ? 'Personal' : 'Brokerage'}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="drip-field">
                      <label>Send Date</label>
                      <input
                        type="date"
                        value={email.dripDate}
                        onChange={(e) => handleDripChange(index, 'dripDate', e.target.value)}
                        className="date-input"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowDripModal(false)} className="cancel-btn">
                Cancel
              </button>
              <button
                onClick={scheduleDrip}
                disabled={schedulingDrip || !emailAddresses.trim()}
                className="save-btn"
              >
                {schedulingDrip ? 'Scheduling...' : 'Schedule Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSenderModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Sender Email Configuration</h3>
              <button onClick={() => setShowSenderModal(false)} className="close-btn">
                ×
              </button>
            </div>
            <div className="sender-form">
              <VerifySenderButton />
              <div className="form-field">
                <label>Email Address</label>
                <input
                  type="email"
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value.replace(/\s/g, ''))}
                  placeholder="sender@gmail.com"
                  className="sender-input"
                />
              </div>
              <div className="form-field">
                <div className="password-label-container">
                  <label>Email Password</label>
                  <button
                    type="button"
                    onClick={() => setShowAppPasswordHelp(!showAppPasswordHelp)}
                    className="info-btn"
                    title="App password help"
                  >
                    i
                  </button>
                </div>
                <input
                  type="password"
                  value={senderPassword}
                  onChange={(e) => setSenderPassword(e.target.value.replace(/\s/g, ''))}
                  placeholder="App password"
                  className="sender-input"
                />
                <div className="helper-text">App password is not the same as your login password.</div>
                {showAppPasswordHelp && (
                  <div className="app-password-help">
                    <h4>How to get Google App Password:</h4>
                    <ol>
                      <li>Log into your Google account</li>
                      <li>Click on Account</li>
                      <li>Click on Security</li>
                      <li>Ensure 2FA is active</li>
                      <li>In the top search bar, search "app password"</li>
                      <li>Setup app password and save it somewhere safe</li>
                      <li>Use that app password here</li>
                    </ol>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowSenderModal(false)} className="cancel-btn">
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (senderEmail && senderPassword) {
                    try {
                      const res = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/feature-flag?siteId=${SITE_CONFIG.SITE_ID}&senderEmail=${encodeURIComponent(senderEmail)}&senderEmailPassword=${encodeURIComponent(senderPassword)}`, {
                        method: 'POST',
                        headers: { 'X-API-Key': 'treas-api-key-2025' }
                      });
                      if (res.ok) showToast('Sender email saved successfully!', 'success');
                      else showToast('Failed to save sender email', 'error');
                    } catch (e) { showToast('Error saving sender config', 'error'); }
                  }
                  setShowSenderModal(false);
                  setShowSendEmailModal(true);
                }}
                className="save-btn"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showSignatureModal && (
        <div className="modal">
          <div className="modal-content signature-modal-content">
            <div className="modal-header">
              <div>
                <span className="email-eyebrow">Agent identity</span>
                <h3>{signatureConfigured ? 'Edit email signature' : 'Set up email signature'}</h3>
              </div>
              <button type="button" onClick={() => setShowSignatureModal(false)} className="close-btn" aria-label="Close signature setup">
                ×
              </button>
            </div>

            {signatureLoading ? (
              <div className="signature-loading">Loading your signature…</div>
            ) : (
              <form onSubmit={handleSaveSignature}>
                <div className="signature-form">
                  <p className="signature-intro">These details create the professional signature attached to your emails.</p>
                  {signatureError && <div className="signature-error" role="alert">{signatureError}</div>}

                  <div className="signature-fields-grid">
                    <label>
                      <span>Name <em>Required</em></span>
                      <input type="text" value={signatureForm.name} onChange={event => updateSignatureField('name', event.target.value)} autoComplete="name" required />
                    </label>
                    <label>
                      <span>Phone</span>
                      <input type="tel" value={signatureForm.phone} onChange={event => updateSignatureField('phone', event.target.value)} autoComplete="tel" />
                    </label>
                    <label>
                      <span>Email <em>Required</em></span>
                      <input type="email" value={signatureForm.email} onChange={event => updateSignatureField('email', event.target.value)} autoComplete="email" required />
                    </label>
                    <label>
                      <span>Brokerage <em>Required</em></span>
                      <input type="text" value={signatureForm.brokerage} onChange={event => updateSignatureField('brokerage', event.target.value)} autoComplete="organization" required />
                    </label>
                    <label>
                      <span>City</span>
                      <input type="text" value={signatureForm.city} onChange={event => updateSignatureField('city', event.target.value)} autoComplete="address-level2" />
                    </label>
                    <label>
                      <span>State</span>
                      <input type="text" value={signatureForm.state} onChange={event => updateSignatureField('state', event.target.value.toUpperCase())} autoComplete="address-level1" maxLength="2" />
                    </label>
                  </div>

                  <div className="signature-documents">
                    <div>
                      <h4>Legal documents</h4>
                      <p>Optional. Paste the public HTTPS link for each document you want included.</p>
                    </div>
                    {SIGNATURE_DOCUMENTS.map(document => (
                      <label key={document.key}>
                        <span>{document.label}</span>
                        <input
                          type="url"
                          value={signatureForm.documentUrls[document.key]}
                          onChange={event => updateSignatureDocument(document.key, event.target.value)}
                          placeholder="https://example.com/document.pdf"
                        />
                      </label>
                    ))}
                  </div>
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={() => setShowSignatureModal(false)} className="cancel-btn" disabled={signatureSaving}>Cancel</button>
                  <button type="submit" className="save-btn" disabled={signatureSaving}>
                    {signatureSaving ? 'Saving signature…' : 'Save signature'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {showSendEmailModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Send Email</h3>
              <button onClick={() => setShowSendEmailModal(false)} className="close-btn">
                ×
              </button>
            </div>
            <div className="send-email-form">
              <div className="form-field">
                <label>Recipient Email</label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="recipient@email.com"
                  className="recipient-input"
                />
              </div>
              <div className="form-field">
                <div className="sender-config">
                  <label>Sender Configuration (Optional)</label>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSendEmailModal(false);
                      setShowSenderModal(true);
                    }}
                    className="sender-config-btn"
                  >
                    Configure Sender Email
                  </button>
                </div>
                {senderEmail && (
                  <div className="current-sender">
                    Current sender: {senderEmail}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowSendEmailModal(false)} className="cancel-btn">
                Cancel
              </button>
              <button
                onClick={sendEmail}
                disabled={sendingEmail || !recipientEmail.trim()}
                className="save-btn"
              >
                {sendingEmail ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showListingKeyModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Create Email from Listing Key</h3>
              <button onClick={() => setShowListingKeyModal(false)} className="close-btn">
                ×
              </button>
            </div>
            <div className="listing-key-form">
              <div className="form-field">
                <label>Listing Key</label>
                <input
                  type="text"
                  value={listingKey}
                  onChange={(e) => setListingKey(e.target.value)}
                  placeholder="Enter listing key (e.g., 274349384)"
                  className="listing-key-input"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowListingKeyModal(false)} className="cancel-btn">
                Cancel
              </button>
              <button
                onClick={createFromListingKey}
                disabled={loadingListingData || !listingKey.trim()}
                className="save-btn"
              >
                {loadingListingData ? 'Loading Property Data...' : 'Create Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showGenerateModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Generate Template with AI</h3>
              <button onClick={() => setShowGenerateModal(false)} className="close-btn">
                ×
              </button>
            </div>
            <div className="listing-key-form">
              <div className="form-field">
                <label>Signature and Access</label>
                <TemplateScopeSelector
                  name="generate-template-scope"
                  value={templateScope}
                  onChange={setTemplateScope}
                  personalDisabled={!currentUserId}
                  disabled={generatingTemplate}
                />
              </div>
              <div className="form-field">
                <label>Describe the email you want</label>
                <textarea
                  value={generatePrompt}
                  onChange={(e) => setGeneratePrompt(e.target.value)}
                  placeholder="e.g., Create a concise spring home maintenance email for homeowners"
                  className="generate-ai-textarea"
                  rows="4"
                />
              </div>
              <div className="form-field">
                <label>Recipient Name (optional — leave blank for a client-agnostic bulk email)</label>
                <input
                  type="text"
                  value={generateRecipientName}
                  onChange={(e) => setGenerateRecipientName(e.target.value)}
                  placeholder="e.g., Taylor"
                  className="listing-key-input"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowGenerateModal(false)} className="cancel-btn">
                Cancel
              </button>
              <button
                onClick={generateAiTemplate}
                disabled={generatingTemplate || !generatePrompt.trim() || (templateScope === 'agent' && !currentUserId)}
                className="save-btn"
              >
                {generatingTemplate ? 'Generating... (may take ~10s)' : 'Generate Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default EmailManager;
